import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import roleService from '../../core/services/RoleService';
import useUsers from './../hooks/useUsers';
import { fetchPermissions } from '../../core/store/actions/permissions';
import { hasManageAccessControlPermission } from '../../core/utils/permissionUtils';
import CustomAlert from '../CustomAlert';
import CustomAlertWithButtons from '../CustomAlertWithButtons';
import './../../styles/RoleManagement.css';
import LoadingSpinner from '../LoadingSpinner';
import NavHeader from '../NavHeader';

function RoleManagement() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.profile);
  const permissionsState = useSelector((state) => state.permissions);
  const { userOptions, loading: usersLoading } = useUsers();
  const isMountedRef = useRef(true);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesRefreshing, setRolesRefreshing] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePositionEmail, setNewRolePositionEmail] = useState('');
  const [positionEmailDrafts, setPositionEmailDrafts] = useState({});
  const [isSavingPositionEmail, setIsSavingPositionEmail] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAssigning, setIsAssigning] = useState({});
  const [isRemovingUser, setIsRemovingUser] = useState({});
  const [isRemovingPermission, setIsRemovingPermission] = useState({});
  const [selectedUserForRole, setSelectedUserForRole] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Permissions state
  const [selectedPermissionsForRole, setSelectedPermissionsForRole] = useState({});
  const [isAssigningPermissions, setIsAssigningPermissions] = useState({});

  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: '',
    type: '',
    message: '',
    btn1Action: () => { },
    btn2Action: () => { },
     button1Text: '',
     button2Text: ''
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRoleId, setExpandedRoleId] = useState(null);

  const formatUserLabel = (userEntry) => {
    try {
      return userEntry.label || userEntry.userData.name || userEntry.userData.email || 'Unknown user';
    } catch (error) {
      return userEntry.fullname || userEntry.name || userEntry.email || 'Unknown user';
    }
  };

  const getAssignedUserFirstLastName = (assignedUser) => {
    if (!assignedUser) return null;
    return assignedUser.firstLastName?.trim()
      || assignedUser.fullName
      || assignedUser.name
      || assignedUser.email
      || null;
  };

  const getAssignedUserFullName = (assignedUser) => {
    if (!assignedUser) return 'Unknown user';
    return assignedUser.fullName
      || assignedUser.name
      || assignedUser.email
      || 'Unknown user';
  };

  const formatPermissionLabel = (permissionEntry) => {
    return permissionEntry.action_key || permissionEntry.name || 'Unknown permission';
  };

  const permissionsLoading = permissionsState.loading;
  const shouldFetchPermissions = !Array.isArray(permissionsState.items) || permissionsState.items.length === 0;
  const allPermissions = useMemo(() => {
    return (permissionsState.items || []).map((permission) => ({
      value: permission._id,
      label: formatPermissionLabel(permission),
      data: permission
    }));
  }, [permissionsState.items]);

  const permissionsByRoleId = useMemo(() => {
    const map = new Map();
    (permissionsState.items || []).forEach((permission) => {
      (permission.roles || []).forEach((role) => {
        const roleId = typeof role === 'string' ? role : role?._id;
        if (!roleId) return;
        if (!map.has(roleId)) {
          map.set(roleId, []);
        }
        map.get(roleId).push(permission);
      });
    });
    return map;
  }, [permissionsState.items]);

  const rolesListRef = useRef(null);

  const fetchRoles = useCallback(async ({ showLoading = true } = {}) => {
    try {
      // Check permission before making API calls
      if (!hasManageAccessControlPermission(user)) {
        if (isMountedRef.current) {
          setAlertInfo({
            show: true,
            type: 'error',
            message: 'You do not have permission to access this page. Required permission: MANAGE_ACCESS_CONTROL'
          });
          setLoading(false);
        }
        return;
      }

      if (isMountedRef.current && showLoading) setLoading(true);
      const rolesResponse = await roleService.getAllRoles().catch(err => {
        console.error('Error fetching roles:', err);
        return [];
      });

      if (shouldFetchPermissions) {
        await dispatch(fetchPermissions()).catch((err) => {
          console.error('Error fetching permissions:', err);
          return [];
        });
      }

      if (isMountedRef.current) {
        const rolesData = Array.isArray(rolesResponse)
          ? rolesResponse
          : (rolesResponse.data || []);
        setRoles(rolesData);

      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: 'Error loading roles or permissions. Please try again.'
        });
      }
    } finally {
      if (isMountedRef.current && showLoading) setLoading(false);
      if (isMountedRef.current && !showLoading) setRolesRefreshing(false);
    }
  }, [user, dispatch, shouldFetchPermissions]);

  // const refreshRolesPreserveScroll = useCallback(async () => {
  //   const listEl = rolesListRef.current;
  //   const scrollTop = listEl ? listEl.scrollTop : 0;

  //   await fetchRoles({ showLoading: false });

  //   if (listEl) {
  //     listEl.scrollTop = scrollTop;
  //   }
  // }, [fetchRoles]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchRoles();
    return () => {
      isMountedRef.current = false;
    };
  }, [user, fetchRoles]);

  // Debug: Log roles changes
  useEffect(() => {
    // console.log('Roles state updated:', roles, 'Loading:', loading, 'Modal open:', showCreateModal);
  }, [roles, loading, showCreateModal]);

  // Only set loading if we don't have roles yet
  useEffect(() => {
    if (roles.length === 0) {
      setLoading(usersLoading);
    }
  }, [usersLoading, roles.length]);

  const handleCreateRole = async (e) => {
    e.preventDefault();

    // Validation
    if (!newRoleName.trim()) {
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: 'Role name cannot be empty.'
        });
      }
      return;
    }

    try {
      if (isMountedRef.current) setIsSaving(true);
      const createPayload = { name: newRoleName.trim() };
      if (newRolePositionEmail.trim()) {
        createPayload.positionEmail = newRolePositionEmail.trim();
      }
      const new_role = await roleService.createRole(createPayload);
      
      if (!isMountedRef.current) return;
      
      if (isMountedRef.current) {
        // Ensure loading is false so the table displays
        setLoading(false);
        setRoles(prevRoles => {
          const updated = [new_role, ...prevRoles];
          // console.log('Updated roles:', updated);
          return updated;
        });
        // Close modal AFTER state update to ensure visibility
        setTimeout(() => {
          if (isMountedRef.current) {
            handleCloseCreateModal(true);
          }
        }, 50);
      }
    } catch (error) {
      console.error('Error creating role:', error);
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: error.message || 'Error creating role. Please try again.'
        });
      }
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = async (success = false) => {
    if (!isMountedRef.current) return;
    
    // console.log('Closing modal with success:', success);
    setShowCreateModal(false);
    setNewRoleName('');
    setNewRolePositionEmail('');
    
    // Wait for modal to fully close before showing alert
    if (success) {
      setTimeout(() => {
        if (isMountedRef.current) {
          setAlertInfo({
            show: true,
            type: 'success',
            message: 'The role was successfully created!'
          });
        }
      }, 300);
    }
  };
  
  const handleDeleteRole = useCallback((role) => {
    if (!isMountedRef.current) return;
    
    if (role.isProtected) {
      setAlertInfo({
        show: true,
        type: 'warning',
        message: `Role "${role.name}" is protected and cannot be deleted.`
      });
      return;
    }

    setAlertActionInfo({
      show: true,
      title: `Delete Role "${role.name}"`,
      type: 'error',
      message: `Are you sure? This action cannot be undone.`,
      btn2Action: async () => {
        try {
          if (isMountedRef.current) setIsDeleting(true);
          await roleService.deleteRole(role._id);

          if (isMountedRef.current) {
            setRoles((prevRoles) =>
              prevRoles.filter((role_check) => role_check._id !== role._id)
            );
            setAlertInfo({
              show: true,
              type: 'success',
              message: `Role "${role.name}" deleted successfully.`
            });
          }

        } catch (error) {
          if (isMountedRef.current) {
            setAlertInfo({
              show: true,
              type: 'error',
              message: `Failed to delete role: ${error.message}`,
            });
          }
        } finally {
          if (isMountedRef.current) {
            setIsDeleting(false);
            setAlertActionInfo((prevState) => ({
              ...prevState,
              show: false,
            }));
          }
        }
      },
      btn1Action: () => {
        if (isMountedRef.current) {
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Text: 'No',
      btn2Text: 'Yes, delete it',
    });
  }, [dispatch]);

  const toggleRoleExpanded = (roleId) => {
    setExpandedRoleId((currentRoleId) => {
      if (currentRoleId === roleId) {
        return null;
      }
      const role = roles.find((entry) => entry._id === roleId);
      if (role) {
        setPositionEmailDrafts((prev) => ({
          ...prev,
          [roleId]: role.positionEmail || ''
        }));
      }
      return roleId;
    });
  };

  const getPositionEmailDraft = (role) => {
    if (Object.prototype.hasOwnProperty.call(positionEmailDrafts, role._id)) {
      return positionEmailDrafts[role._id];
    }
    return role.positionEmail || '';
  };

  const handlePositionEmailDraftChange = (roleId, value) => {
    setPositionEmailDrafts((prev) => ({ ...prev, [roleId]: value }));
  };

  const handleSavePositionEmail = async (role) => {
    const draft = getPositionEmailDraft(role).trim();
    const current = (role.positionEmail || '').trim();

    if (draft === current) {
      return;
    }

    try {
      if (isMountedRef.current) {
        setIsSavingPositionEmail((prev) => ({ ...prev, [role._id]: true }));
      }
      const updatedRole = await roleService.updateRole(role._id, {
        positionEmail: draft
      });

      if (isMountedRef.current) {
        setRoles((prevRoles) =>
          prevRoles.map((entry) => (
            entry._id === role._id
              ? { ...entry, positionEmail: updatedRole.positionEmail || '' }
              : entry
          ))
        );
        setPositionEmailDrafts((prev) => ({
          ...prev,
          [role._id]: updatedRole.positionEmail || ''
        }));
        setAlertInfo({
          show: true,
          type: 'success',
          message: `Position email updated for "${role.name}".`
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: error.message || 'Failed to update position email.'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsSavingPositionEmail((prev) => ({ ...prev, [role._id]: false }));
      }
    }
  };

  const getFilteredRoles = useCallback(() => {
    if (!searchQuery.trim()) {
      return roles;
    }
    const lowerCaseQuery = searchQuery.toLowerCase();
    return roles.filter((role) =>
      role.name.toLowerCase().includes(lowerCaseQuery)
      || (role.positionEmail || '').toLowerCase().includes(lowerCaseQuery)
    );
  }, [roles, searchQuery]);

  const handleAssignUserToRole = async (roleId, userId) => {
    if (!userId) {
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: 'Please select a user to assign to this role.'
        });
      }
      return;
    }

    try {
      if (isMountedRef.current) setIsAssigning({ ...isAssigning, [roleId]: true });
      
      const assignedUser = userOptions.find(u => u.value === userId);
      console.log(`Assigning user to role ID ${roleId}:`, assignedUser);
      const data = await roleService.assignUserToRole(roleId, userId);

      if (!isMountedRef.current) return;

      setRoles((prevRoles) => {
        return prevRoles.map((r) => {
          if (r._id === roleId) {
            // Construct user object with data returned from the server
            const userToAdd = {
              _id: data.assignedToUserId || userId,
              email: data.assignedToUserEmail,
              name: data.assignedToUserName,
              fullName: data.assignedToUserFullName,
              firstLastName: data.assignedToUserFirstLastName,
              label: data.assignedToUserFirstLastName || data.assignedToUserFullName || data.assignedToUserName || data.assignedToUserEmail
            };
            return {
              ...r,
              assignedUsers: [...(r.assignedUsers || []), userToAdd]
            };
          }
          return r;
        });
      });
      
      setAlertInfo({
        show: true,
        type: 'success',
        message: `Role successfully assigned to ${assignedUser.fullName || assignedUser.email}`
      });
    } catch (error) {
      console.error('Error assigning user to role:', error);
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: error.message || 'Error assigning user to role. Please try again.'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsAssigning({ ...isAssigning, [roleId]: false });
      }
    }
  };

  const updateRolePermissionsAfterAssign = (roleId, assignedPermissions, sourcePerms) => {
    setRoles((prevRoles) => {
      return prevRoles.map((r) => {
        if (r._id === roleId) {
          const addedPermissions = assignedPermissions.map((p) => ({
            _id: p.id,
            action_key: p.action_key,
            description: sourcePerms.find((sp) => sp.value === p.id)?.data?.description
          }));

          const currentPermIds = new Set((r.permissions || []).map((p) => p._id));
          const newPerms = addedPermissions.filter((p) => !currentPermIds.has(p._id));

          return {
            ...r,
            permissions: [...(r.permissions || []), ...newPerms]
          };
        }
        return r;
      });
    });
  };

  const handleAssignPermissionsToRole = async (roleId) => {
    const selectedPerms = selectedPermissionsForRole[roleId];
    if (!selectedPerms || selectedPerms.length === 0) {
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: 'Please select at least one permission to assign.'
        });
      }
      return;
    }

    const permissionIds = selectedPerms.map(p => p.value);

    try {
      if (isMountedRef.current) setIsAssigningPermissions({ ...isAssigningPermissions, [roleId]: true });
      
      const data = await roleService.assignPermissionsToRole(roleId, permissionIds);

      if (!isMountedRef.current) return;

      updateRolePermissionsAfterAssign(roleId, data.assignedPermissions, selectedPerms);
      
      setAlertInfo({
        show: true,
        type: 'success',
        message: `Permissions successfully assigned.`
      });

      await dispatch(fetchPermissions());
      
      setSelectedPermissionsForRole({ ...selectedPermissionsForRole, [roleId]: null });
    } catch (error) {
      console.error('Error assigning permissions to role:', error);
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: error.message || 'Error assigning permissions to role. Please try again.'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsAssigningPermissions({ ...isAssigningPermissions, [roleId]: false });
      }
    }
  };

  const handleAddAllPermissionsToRole = async (roleId, currentPermissions) => {
    const assignedIds = new Set(
      currentPermissions.map((permission) => permission._id || permission.id)
    );
    const unassignedPermissions = allPermissions.filter(
      (permission) => !assignedIds.has(permission.value)
    );

    if (unassignedPermissions.length === 0) {
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'warning',
          message: 'All permissions are already assigned to this role.'
        });
      }
      return;
    }

    const permissionIds = unassignedPermissions.map((permission) => permission.value);

    try {
      if (isMountedRef.current) setIsAssigningPermissions({ ...isAssigningPermissions, [roleId]: true });

      const data = await roleService.assignPermissionsToRole(roleId, permissionIds);

      if (!isMountedRef.current) return;

      updateRolePermissionsAfterAssign(roleId, data.assignedPermissions, unassignedPermissions);

      setAlertInfo({
        show: true,
        type: 'success',
        message: `All ${unassignedPermissions.length} permissions successfully assigned.`
      });

      await dispatch(fetchPermissions());

      setSelectedPermissionsForRole({ ...selectedPermissionsForRole, [roleId]: null });
    } catch (error) {
      console.error('Error assigning all permissions to role:', error);
      if (isMountedRef.current) {
        setAlertInfo({
          show: true,
          type: 'error',
          message: error.message || 'Error assigning permissions to role. Please try again.'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsAssigningPermissions({ ...isAssigningPermissions, [roleId]: false });
      }
    }
  };

  const handleRemoveUserFromRole = useCallback((role, user) => {
    if (!isMountedRef.current) return;

    setAlertActionInfo({
      show: true,
      title: `Remove ${user.fullName} from "${role.name}"`,
      type: 'error',
      message: `Are you sure?`,
      btn2Action: async () => {
        try {
          if (isMountedRef.current) setIsRemovingUser({ ...isRemovingUser, [`${role._id}-${user._id}`]: true });
          
          const updated_data = await roleService.removeUserFromRole(role._id, user._id);

          if (isMountedRef.current) {
            setRoles((prevRoles) => {
              return prevRoles.map((r) => {
                if (r._id === role._id) {
                  const newAssignedUsers = r.assignedUsers.filter((u) => u._id !== updated_data.removedUserId);
                  return { ...r, assignedUsers: newAssignedUsers };
                }
                return r;
              });
            });

            setAlertInfo({
              show: true,
              type: 'success',
              message: `${formatUserLabel(user)} has been removed from the role "${role.name}".`
            });
          }
        } catch (error) {
          console.error('Error removing user from role:', error);
          if (isMountedRef.current) {
            setAlertInfo({
              show: true,
              type: 'error',
              message: error.message || 'Error removing user from role. Please try again.'
            });
          }
        } finally {
          if (isMountedRef.current) {
            setIsRemovingUser({ ...isRemovingUser, [`${role._id}-${user._id}`]: false });
            setAlertActionInfo((prevState) => ({
              ...prevState,
              show: false,
            }));
          }
        }
      },
      btn1Action: () => {
        if (isMountedRef.current) {
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Text: 'No',
      btn2Text: 'Yes, remove it',
    });
  }, [isRemovingUser, fetchRoles, dispatch]);

  const handleRemovePermissionFromRole = useCallback((role, permission) => {
    if (!isMountedRef.current) return;

    setAlertActionInfo({
      show: true,
      title: `Remove ${formatPermissionLabel(permission)} from ${role.name}`,
      type: 'error',
      message: `Are you sure?`,
      btn2Action: async () => {
        try {
          if (isMountedRef.current) setIsRemovingPermission({ ...isRemovingPermission, [`${role._id}-${permission._id}`]: true });
          
          await roleService.removePermissionFromRole(role._id, permission._id);

          if (isMountedRef.current) {
            setRoles((prevRoles) => {
              return prevRoles.map((r) => {
                if (r._id === role._id) {
                  const newPermissions = r.permissions.filter((p) => p._id !== permission._id);
                  return { ...r, permissions: newPermissions };
                }
                return r;
              });
            });

            setAlertInfo({
              show: true,
              type: 'success',
              message: `The permission "${formatPermissionLabel(permission)}" has been removed from the role "${role.name}".`
            });

            await dispatch(fetchPermissions());
          }
        } catch (error) {
          console.error('Error removing permission from role:', error);
          if (isMountedRef.current) {
            setAlertInfo({
              show: true,
              type: 'error',
              message: error.message || 'Error removing permission from role. Please try again.'
            });
          }
        } finally {
          if (isMountedRef.current) {
            setIsRemovingPermission({ ...isRemovingPermission, [`${role._id}-${permission._id}`]: false });
            setAlertActionInfo((prevState) => ({
              ...prevState,
              show: false,
            }));
          }
        }
      },
      btn1Action: () => {
        if (isMountedRef.current) {
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Text: 'No',
      btn2Text: 'Yes, remove it',
    });
  }, [isRemovingPermission, dispatch]);

  return (
    <div className="page-container">
      <NavHeader />
      <div className="role-management-container">
        {alertInfo.show && (
          <CustomAlert
            type={alertInfo.type}
            message={alertInfo.message}
            onClose={() => setAlertInfo({ ...alertInfo, show: false })}
          />
        )}

        {alertActionInfo.show && (
          <CustomAlertWithButtons
            title={alertActionInfo.title}
            type={alertActionInfo.type}
            message={alertActionInfo.message}
            button1Text={alertActionInfo.btn1Text}
            button2Text={alertActionInfo.btn2Text}
            onButton1Click={alertActionInfo.btn1Action}
            onButton2Click={alertActionInfo.btn2Action}
          />
        )}

        <div className="role-management-header">
          <h1 className="page-title">Role Management</h1>
        </div>

        {/* Search Bar Section */}
        <div className="role-search-section">
          <div className="search-input-wrapper">
            <Icon icon="f7:magnifyingglass" className="search-icon" />
            <input
              type="text"
              className="role-search-input"
              placeholder="Search roles by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear-button"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <Icon icon="f7:xmark-circle-fill" />
              </button>
            )}
          </div>
        </div>

        <Modal
          centered
          show={showCreateModal}
          onHide={handleCloseCreateModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Create New Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleCreateRole} className="create-role-form">
              <Form.Group controlId="formRoleNameModal" className="form-group">
                <label htmlFor="roleNameModal">Role Name</label>
                <input
                  className="form-input"
                  type="text"
                  name="roleName"
                  id="roleNameModal"
                  placeholder="Enter role name (e.g., Project Lead)"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  disabled={isSaving}
                  autoFocus
                />
              </Form.Group>
              <Form.Group controlId="formRolePositionEmailModal" className="form-group">
                <label htmlFor="rolePositionEmailModal">Position email</label>
                <input
                  className="form-input"
                  type="email"
                  name="rolePositionEmail"
                  id="rolePositionEmailModal"
                  placeholder="e.g. wpa@esnlisboa.org"
                  value={newRolePositionEmail}
                  onChange={(e) => setNewRolePositionEmail(e.target.value)}
                  disabled={isSaving}
                />
              </Form.Group>
              <div className="modal-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseCreateModal}
                  disabled={isSaving}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={isSaving || !newRoleName.trim()}
                >
                  {isSaving ? (
                    <>
                      <Icon icon="eos-icons:loading" className="loading-icon" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Icon icon="f7:plus-circle-fill" className="btn-icon" />
                      Create Role
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Roles List Section */}
        <div className="roles-list-section">
          <div className="section-title-row">
            <div className="section-title">
              <Icon icon="f7:list-bullet-circle-fill" className="section-icon" />
              Existing Roles ({getFilteredRoles().length})
              {rolesRefreshing && (
                <span style={{ marginLeft: '10px', fontSize: '0.85rem', color: '#6b7280' }}>
                  <Icon icon="eos-icons:loading" style={{ marginRight: '6px' }} />
                  Refreshing...
                </span>
              )}
            </div>
            <Button
              type="button"
              className="btn-primary create-role-button"
              onClick={handleOpenCreateModal}
            >
              <Icon icon="f7:plus-circle-fill" className="btn-icon" />
              Create New Role
            </Button>
          </div>

          {loading && roles.length === 0 ? (
            <div className="roles-list" ref={rolesListRef}>
              <LoadingSpinner message="Loading roles" />
            </div>
          ) : roles.length === 0 ? (
            <div className="empty-state">
              <Icon icon="f7:circle-slash" className="empty-icon" />
              <p>No roles found. Create one to get started.</p>
            </div>
          ) : getFilteredRoles().length === 0 ? (
            <div className="empty-state">
              <Icon icon="f7:circle-slash" className="empty-icon" />
              <p>No roles match your search. Try different keywords.</p>
            </div>
          ) : (
            <div className="roles-list" ref={rolesListRef}>
              {getFilteredRoles().map((role) => {
                const assignedUsers = role.assignedUsers || [];
                const rolePermissions = Array.isArray(role.permissions) ? role.permissions : [];
                const storePermissions = permissionsByRoleId.get(role._id) || [];
                const permissions = (() => {
                  const merged = new Map();
                  [...rolePermissions, ...storePermissions].forEach((permission) => {
                    if (!permission) return;
                    const key = permission._id || permission.id || permission.action_key;
                    if (!key) return;
                    merged.set(key, permission);
                  });
                  return Array.from(merged.values());
                })();
                const isExpanded = expandedRoleId === role._id;
                const hasAssignedUser = assignedUsers.length > 0;
                const assignedUserName = hasAssignedUser
                  ? getAssignedUserFirstLastName(assignedUsers[0])
                  : null;

                return (
                  <div
                    key={role._id}
                    className={`role-card ${role.isProtected ? 'protected-role' : ''} ${isExpanded ? 'expanded' : ''} ${!hasAssignedUser ? 'unassigned-role' : ''}`}
                  >
                    <div
                      className="role-header"
                      onClick={() => toggleRoleExpanded(role._id)}
                    >
                      <div className="role-info">
                        <h3 className="role-key">
                          {role.isProtected && (
                            <Icon icon="f7:lock-shield-fill" className="protected-icon" title="Protected role" />
                          )}
                          <span className="role-key-name">{role.name}</span>
                          {!isExpanded && assignedUserName && (
                            <span className="role-assigned-user-name">{assignedUserName}</span>
                          )}
                        </h3>
                        <p className="role-card-description">
                          {role.description || 'No description'}
                        </p>
                        {role.positionEmail && !isExpanded && (
                          <p className="role-position-email-preview">
                            <Icon icon="f7:envelope-fill" className="summary-icon" />
                            {role.positionEmail}
                          </p>
                        )}
                      </div>
                      <div className="role-summary">
                        {role.isProtected ? (
                          <span className="badge badge-protected">Protected</span>
                        ) : (
                          <span className="badge badge-custom">Custom</span>
                        )}
                        <span className="role-count">
                          <Icon icon="f7:lock-circle-fill" className="summary-icon" />
                          {permissions.length} permission{permissions.length !== 1 ? 's' : ''}
                        </span>
                        <span className="role-count">
                          <Icon icon="f7:person-fill" className="summary-icon" />
                          {assignedUsers.length} user{assignedUsers.length !== 1 ? 's' : ''}
                        </span>
                        <Icon
                          icon={isExpanded ? 'f7:chevron-up' : 'f7:chevron-down'}
                          className="expand-icon"
                        />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="role-details">
                        <div className="role-usage-panel">
                          <div className="role-usage-section">
                            <div className="role-usage-title">
                              Position email
                            </div>
                            <div className="role-position-email-form">
                              <input
                                className="form-input"
                                type="email"
                                placeholder="e.g. wpa@esnlisboa.org"
                                value={getPositionEmailDraft(role)}
                                onChange={(e) => handlePositionEmailDraftChange(role._id, e.target.value)}
                                disabled={isSavingPositionEmail[role._id]}
                              />
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleSavePositionEmail(role)}
                                disabled={isSavingPositionEmail[role._id]}
                                className="btn-assign btn-assign-full"
                              >
                                {isSavingPositionEmail[role._id] ? (
                                  <>
                                    <Icon icon="eos-icons:loading" className="loading-icon" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <Icon icon="f7:checkmark-circle-fill" className="btn-icon" />
                                    Save position email
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                          <div className="role-usage-section">
                            <div className="role-usage-title">
                              User Assignment
                            </div>
                            <div className="role-user-assignment">
                              {assignedUsers.length === 0 ? (
                                <div className="role-usage-empty">No user assigned.</div>
                              ) : (
                                <ul className="role-usage-list">
                                  {assignedUsers.map((assignedUser) => (
                                    <li key={assignedUser._id} className="role-usage-item">
                                      <div className="role-usage-item-content">
                                        <div className="role-usage-item-text">
                                          <span className="role-usage-label">
                                            {getAssignedUserFullName(assignedUser)}
                                          </span>
                                          {assignedUser.email && (
                                            <span className="role-usage-meta">{assignedUser.email}</span>
                                          )}
                                        </div>
                                        <button
                                          className="role-usage-remove-btn"
                                          onClick={() => handleRemoveUserFromRole(role, assignedUser)}
                                          disabled={isRemovingUser[`${role._id}-${assignedUser._id}`]}
                                          title="Remove this user from the role"
                                        >
                                          {isRemovingUser[`${role._id}-${assignedUser._id}`] ? (
                                            <Icon icon="eos-icons:loading" className="remove-icon-loading" />
                                          ) : (
                                            <Icon icon="f7:trash-circle-fill" className="remove-icon" />
                                          )}
                                        </button>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {assignedUsers.length === 0 && (
                                <div className="role-user-selector-form">
                                  <div className="selector-group selector-group-vertical">
                                    <Select
                                      className="user-select"
                                      classNamePrefix="user-select"
                                      isDisabled={isAssigning[role._id] || userOptions.length === 0 || usersLoading}
                                      placeholder={usersLoading ? 'Loading users...' : 'Search and select user'}
                                      options={userOptions}
                                      value={selectedUserForRole[role._id] ? userOptions.find(u => u.value === selectedUserForRole[role._id]) : null}
                                      onChange={(selectedOption) => setSelectedUserForRole({ ...selectedUserForRole, [role._id]: selectedOption?.value || '' })}
                                    />
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => handleAssignUserToRole(role._id, selectedUserForRole[role._id])}
                                      disabled={isAssigning[role._id] || !selectedUserForRole[role._id]}
                                      className="btn-assign btn-assign-full"
                                    >
                                      {isAssigning[role._id] ? (
                                        <>
                                          <Icon icon="eos-icons:loading" className="loading-icon" />
                                          Assigning...
                                        </>
                                      ) : (
                                        <>
                                          <Icon icon="f7:person-fill-checkmark" className="btn-icon" />
                                          Assign User
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="role-usage-section">
                            <div className="role-usage-title">
                              Permissions ({permissions.length})
                            </div>
                            {permissions.length === 0 ? (
                              <div className="role-usage-empty">No permissions assigned.</div>
                            ) : (
                              <ul className="role-usage-list permissions-list">
                                {permissions.map((permission) => (
                                  <li key={permission._id} className="role-usage-item">
                                    <div className="role-usage-item-content">
                                      <div className="role-usage-item-text">
                                        <span className="role-usage-label">
                                          {formatPermissionLabel(permission)}
                                        </span>
                                        {permission.description && (
                                          <span className="role-usage-meta">{permission.description}</span>
                                        )}
                                      </div>
                                      <button
                                        className="role-usage-remove-btn"
                                        onClick={() => handleRemovePermissionFromRole(role, permission)}
                                        disabled={isRemovingPermission[`${role._id}-${permission._id}`]}
                                        title="Remove this permission from the role"
                                      >
                                        {isRemovingPermission[`${role._id}-${permission._id}`] ? (
                                          <Icon icon="eos-icons:loading" className="remove-icon-loading" />
                                        ) : (
                                          <Icon icon="f7:trash-circle-fill" className="remove-icon" />
                                        )}
                                      </button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="role-user-selector-form">
                              <div className="selector-group selector-group-vertical">
                                <Select
                                  isMulti
                                  className="user-select"
                                  classNamePrefix="user-select"
                                  isDisabled={isAssigningPermissions[role._id] || allPermissions.length === 0 || permissionsLoading}
                                  placeholder={permissionsLoading ? 'Loading permissions...' : 'Search and select permissions'}
                                  options={allPermissions}
                                  value={selectedPermissionsForRole[role._id] || []}
                                  onChange={(selectedOptions) => setSelectedPermissionsForRole({ ...selectedPermissionsForRole, [role._id]: selectedOptions })}
                                />
                                <div className="selector-actions">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleAddAllPermissionsToRole(role._id, permissions)}
                                    disabled={
                                      isAssigningPermissions[role._id]
                                      || allPermissions.length === 0
                                      || permissionsLoading
                                      || permissions.length >= allPermissions.length
                                    }
                                    className="btn-add-all btn-assign-full"
                                    title="Assign all available permissions to this role"
                                  >
                                    {isAssigningPermissions[role._id] ? (
                                      <>
                                        <Icon icon="eos-icons:loading" className="loading-icon" />
                                        Adding...
                                      </>
                                    ) : (
                                      <>
                                        <Icon icon="f7:plus-rectangle-on-rectangle-fill" className="btn-icon" />
                                        Add All
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleAssignPermissionsToRole(role._id)}
                                    disabled={isAssigningPermissions[role._id] || !selectedPermissionsForRole[role._id] || selectedPermissionsForRole[role._id].length === 0}
                                    className="btn-assign btn-assign-full"
                                  >
                                    {isAssigningPermissions[role._id] ? (
                                      <>
                                        <Icon icon="eos-icons:loading" className="loading-icon" />
                                        Assigning...
                                      </>
                                    ) : (
                                      <>
                                        <Icon icon="f7:plus-circle-fill" className="btn-icon" />
                                        Assign
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="role-actions">
                          {!role.isProtected ? (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteRole(role)}
                              disabled={isDeleting}
                              className="btn-delete"
                              title="Delete this role"
                            >
                              <Icon icon="f7:trash-circle-fill" className="btn-icon" />
                              Delete Role
                            </Button>
                          ) : (
                            <div
                              className="protected-tooltip"
                              title="This role is protected by the system and cannot be deleted. It is essential to the organization's governance structure."
                            >
                              <Icon icon="f7:lock-shield-fill" className="info-icon" />
                              <span>Cannot delete protected role</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoleManagement;
